import { useState } from "react";
import { Crown, Headphones, ShieldCheck, Sparkles, X } from "lucide-react";

const AGENT_PROFILES = [
  {
    name: "Fea",
    title: "Game host",
    detail: "Fast account and game help",
    initial: "F",
    avatarClass: "from-[#4c1010] via-[#9f2b1f] to-[#d4af37]",
    image: "/assets/agents/draco.png",
    Icon: Crown,
    aliases: ["DRACO"],
  },
  {
    name: "Mellisa",
    title: "Support lead",
    detail: "Wallet and reward help",
    initial: "M",
    avatarClass: "from-[#06311f] via-[#0f6f48] to-[#d4af37]",
    image: "/assets/agents/mellisa.jpg",
    Icon: ShieldCheck,
    aliases: ["SOPHIA", "MELISSA"],
  },
  {
    name: "Jennifer",
    title: "VIP desk",
    detail: "Redeem and load help",
    initial: "J",
    avatarClass: "from-[#161047] via-[#5b2291] to-[#d4af37]",
    image: "/assets/agents/jennifer.jpg",
    Icon: Sparkles,
    aliases: ["AURUM"],
  },
];

export function getAgentProfile(agent) {
  const name = typeof agent === "string" ? agent : agent?.name;
  if (!name) return null;
  const normalized = String(name).trim().toUpperCase();
  return AGENT_PROFILES.find((profile) => (
    profile.name.toUpperCase() === normalized
    || profile.aliases?.some((alias) => alias.toUpperCase() === normalized)
  )) || null;
}

export default function SupportAgentPicker({ selectedAgent, onPick, className = "" }) {
  const [open, setOpen] = useState(false);
  const selected = getAgentProfile(selectedAgent);

  const choose = (profile) => {
    onPick(profile);
    setOpen(false);
  };

  return (
    <div className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex w-full items-center justify-between gap-3 rounded-2xl border border-[#2F6FA5]/25 bg-white px-3 py-3 text-left shadow-sm hover:border-[#2F6FA5]/60 hover:bg-[#EAF4FC]"
      >
        <div className="flex min-w-0 items-center gap-3">
          {selected ? (
            <AgentAvatar profile={selected} />
          ) : (
            <div className="flex -space-x-3">
              {AGENT_PROFILES.map((profile) => (
                <AgentAvatar key={profile.name} profile={profile} compact />
              ))}
            </div>
          )}
          <div className="min-w-0">
            <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#6EA8D8]">Your best agent</div>
            <div className="truncate text-sm font-bold text-slate-950">
              {selected ? `Chatting with ${selected.name}` : "Choose Fea, Mellisa, or Jennifer"}
            </div>
          </div>
        </div>
        <Headphones className="h-4 w-4 shrink-0 text-[#2F6FA5]" />
      </button>

      {open && (
        <>
          <button
            type="button"
            aria-label="Close agent picker"
            className="fixed inset-0 z-[70] cursor-default bg-transparent"
            onClick={() => setOpen(false)}
          />
          <div className="absolute left-0 right-0 top-full z-[90] mt-2 rounded-2xl border border-[#2F6FA5]/25 bg-white p-3 shadow-2xl shadow-slate-900/15">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div>
                <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#6EA8D8]">Pick your agent</div>
                <div className="text-xs text-slate-500">Choose who you want to message.</div>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="rounded-full p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-950">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {AGENT_PROFILES.map((profile) => {
                const active = selected?.name === profile.name;
                return (
                  <button
                    key={profile.name}
                    type="button"
                    onClick={() => choose(profile)}
                    className={`rounded-2xl border px-2 py-3 text-center transition ${
                      active
                        ? "border-[#2F6FA5] bg-[#EAF4FC]"
                        : "border-slate-200 bg-white hover:border-[#2F6FA5]/60 hover:bg-slate-50"
                    }`}
                  >
                    <AgentAvatar profile={profile} large />
                    <div className="mt-2 text-xs font-black text-slate-950">{profile.name}</div>
                    <div className="mt-0.5 text-[10px] leading-tight text-slate-500">{profile.title}</div>
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function AgentAvatar({ profile, compact = false, large = false }) {
  const Icon = profile.Icon;
  const size = large ? "h-14 w-14" : compact ? "h-9 w-9" : "h-11 w-11";
  const iconSize = large ? "h-4 w-4" : "h-3.5 w-3.5";
  return (
    <div className={`relative mx-auto ${size} shrink-0 overflow-hidden rounded-full border border-[#2F6FA5]/40 bg-gradient-to-br ${profile.avatarClass} p-[2px] shadow-sm`}>
      <div className="grid h-full w-full place-items-center overflow-hidden rounded-full bg-white">
        {profile.image ? (
          <img src={profile.image} alt="" className="h-full w-full object-cover" />
        ) : (
          <span className="font-display text-lg font-black text-[#6EA8D8]">{profile.initial}</span>
        )}
      </div>
      <span className="absolute bottom-0 right-0 grid h-5 w-5 place-items-center rounded-full border border-white bg-[#2F6FA5] text-white">
        <Icon className={iconSize} />
      </span>
    </div>
  );
}
